import { Select, TextInput } from '@octano/global-ui';
import { Fragment, useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import { useFilteredCommunes } from '../../../hooks/useFilteredCommunes';
import { useParameters } from '../../../hooks/useParameters';
import { useValidations } from '../../../hooks/useValidations';
import { SectionTitle } from '../../text';
import useClearInputs from '../hooks/useClearInputs';
import { DynamicContactType } from './RenderSections';

const CHILE_LOWER_CASE = 'chile';

type EducationalBackgroundPDISectionProps = {
  prefix: string;
  type: DynamicContactType;
  optionalFields?: string[];
  fieldNames?: {
    highSchool: string;
    countryHighSchool: string;
    regionHighSchool: string;
    communeHighSchool: string;
    typeHighSchool: string;
    graduationYear: string;
  };
  disabled?: boolean;
};

/**
 * Para usar este componente tu formulario debe estar envuelto en su FormProvider
 * para poder acceder a la información del formulario a través de useFormContext
 * Los nombres agregados en fieldNames se relacionan a los campos que utilizaste
 * al definir tu formulario.
 */
const DynamicEducationalBackgroundPDI = ({
  prefix,
  type,
  optionalFields = [],
  fieldNames = {
    highSchool: 'highSchool',
    countryHighSchool: 'countryHighSchool',
    regionHighSchool: 'regionHighSchool',
    communeHighSchool: 'communeHighSchool',
    typeHighSchool: 'typeHighSchool',
    graduationYear: 'graduationYear',
  },
  disabled = false,
}: EducationalBackgroundPDISectionProps) => {
  const { t } = useTranslation();
  const { control, watch, formState, setValue } = useFormContext();
  const { validateTextNotEmpty } = useValidations();
  const {
    regionOptions,
    countryOptions,
    institutionTypeOptions,
  } = useParameters();

  const [ownSustainer, selectedCountry, selectedRegion] = watch([
    'ownSustainer',
    fieldNames.countryHighSchool,
    fieldNames.regionHighSchool,
  ]);

  const filteredCommunes = useFilteredCommunes(selectedRegion);
  const isRegionTouched = formState.touchedFields[fieldNames.regionHighSchool];
  const isChile = useMemo(() => {
    return String(selectedCountry?.label)
      .toLowerCase()
      .includes(CHILE_LOWER_CASE);
  }, [selectedCountry?.label]);

  const optional = useCallback(
    (value: string, fieldName: string) => {
      if (optionalFields.includes(fieldName)) {
        return undefined;
      }
      return validateTextNotEmpty(value);
    },
    [optionalFields, validateTextNotEmpty],
  );

  const handleChangeOwnSustainer = useCallback(
    (next: boolean) => {
      if (!next) {
        setValue(fieldNames?.regionHighSchool, null);
        setValue(fieldNames?.communeHighSchool, null);
        setValue(fieldNames?.typeHighSchool, '');
        setValue(fieldNames?.graduationYear, '');
      }
    },
    [
      fieldNames?.regionHighSchool,
      fieldNames?.communeHighSchool,
      fieldNames?.typeHighSchool,
      fieldNames?.graduationYear,
      setValue,
    ],
  );

  useClearInputs({ type, handleChangeOwnSustainer: handleChangeOwnSustainer });

  useEffect(() => {
    if (isRegionTouched) {
      setValue(fieldNames.communeHighSchool, null);
    }
  }, [selectedRegion, setValue, isRegionTouched, fieldNames.communeHighSchool]);

  const fields = useMemo(() => {
    return (
      <Fragment>
        <Row className="pt-5">
          <Col xs={12}>
            <SectionTitle text={t(`${prefix}.title`)} />
          </Col>
        </Row>
        <Row>
          <Col className="pb-3" xs={12} lg={8}>
            <TextInput
              name={fieldNames.highSchool}
              label={t(`${prefix}.${fieldNames.highSchool}`)}
              control={control}
              rules={{
                validate: {
                  require: (value) => optional(value, fieldNames.highSchool),
                },
              }}
              readOnly={disabled || ownSustainer === true}
            />
          </Col>
          <Col className="pb-3" xs={12} lg={4}>
            <Select
              name={fieldNames.countryHighSchool}
              label={t(`${prefix}.${fieldNames.countryHighSchool}`)}
              options={countryOptions}
              control={control}
              rules={{
                validate: {
                  require: (value) =>
                    optional(value, fieldNames.countryHighSchool),
                },
              }}
              disabled={disabled || ownSustainer === true}
            />
          </Col>
          {isChile ? (
            <>
              <Col className="pb-3" xs={12} lg={4}>
                <Select
                  name={fieldNames.regionHighSchool}
                  label={t(`${prefix}.${fieldNames.regionHighSchool}`)}
                  options={regionOptions}
                  control={control}
                  rules={{
                    validate: {
                      require: (value) =>
                        optional(value, fieldNames.regionHighSchool),
                    },
                  }}
                  disabled={disabled || ownSustainer === true}
                />
              </Col>
              <Col className="pb-3" xs={12} lg={4}>
                <Select
                  name={fieldNames.communeHighSchool}
                  label={t(`${prefix}.${fieldNames.communeHighSchool}`)}
                  options={filteredCommunes}
                  control={control}
                  rules={{
                    validate: {
                      require: (value) =>
                        optional(value, fieldNames.communeHighSchool),
                    },
                  }}
                  shouldUnregister
                  disabled={disabled || ownSustainer === true}
                />
              </Col>
            </>
          ) : (
            <></>
          )}
          <Col className="pb-3" xs={12} lg={4}>
            <Select
              name={fieldNames.typeHighSchool}
              label={t(`${prefix}.${fieldNames.typeHighSchool}`)}
              options={institutionTypeOptions}
              control={control}
              rules={{
                validate: {
                  require: (value) =>
                    optional(value, fieldNames.typeHighSchool),
                },
              }}
              disabled={disabled || ownSustainer === true}
            />
          </Col>
          <Col className="pb-3" xs={12} lg={4}>
            <TextInput
              name={fieldNames.graduationYear}
              label={t(`${prefix}.${fieldNames.graduationYear}`)}
              control={control}
              readOnly={disabled || ownSustainer === true}
            />
          </Col>
        </Row>
      </Fragment>
    );
  }, [
    fieldNames.countryHighSchool,
    fieldNames.communeHighSchool,
    fieldNames.regionHighSchool,
    fieldNames.typeHighSchool,
    fieldNames.graduationYear,
    fieldNames.highSchool,
    disabled,
    t,
    optional,
    isChile,
    ownSustainer,
    regionOptions,
    countryOptions,
    filteredCommunes,
    institutionTypeOptions,
    control,
    prefix,
  ]);

  return <>{fields}</>;
};

export default DynamicEducationalBackgroundPDI;
